<template>
  <BaseCard
    v-test="'sale-documents'"
    v-component="'sale-documents'"
    :heading="filters.capitalize($t('global.items.document', 2))"
  >
    <EmailReceipt
      v-if="showEmailReceiptModal"
      :email="order?.customer?.email"
      :documentId="selectedDocumentId"
      :orderId="Number(route.params.id)"
      @submit="showEmailReceiptModal = false"
      @close="showEmailReceiptModal = false"
    />
    <PrintUrl
      v-test="'print-url'"
      v-if="promptPrint"
      :key="printKey"
      :url="printUrl"
      @close="promptPrint = false"
    />
    <template #header>
      <BaseSwitch
        v-model="showType"
        v-test="'sale-documents-filter'"
        :options="switchOptions"
      />
    </template>
    <Flex direction="column">
      <Flex
        v-test="'sale-documents-no-results'"
        v-if="!filteredDocuments.length"
        direction="column"
        align="center"
        :padding="2"
      >
        <BaseIcon
          color="secondary"
          name="folder"
          size="xl"
        />
        <Flex
          direction="column"
          align="center"
          :gap="0.5"
        >
          <BaseHeading>{{ noResultsHeading }}</BaseHeading>
          <BaseText color="secondary">{{ noResultsText }}</BaseText>
        </Flex>
      </Flex>
      <BaseCard
        v-for="document in filteredDocuments"
        :key="document.id"
        v-test="'sale-document'"
        :loading="document.id === loadingDocumentId"
      >
        <Flex
          justify="between"
          align="center"
        >
          <Flex
            :gap="0.5"
            align="center"
          >
            <BaseText>
              {{ filters.capitalize($t(`global.items.${document.documentType?.toLowerCase()}`, 1)) }}
            </BaseText>
            <BaseText v-test="'sale-document-number'">
              #{{ document.number }}
            </BaseText>
            <BaseLabel
              v-if="document.legacy"
              v-test="'sale-document-legacy'"
              color="warning"
              inline
            >
              {{ $t('global.legacy') }}
            </BaseLabel>
            <BaseIcon
              v-if="document.legacy"
              :tooltip="$t('register.sale.legacy_info')"
              name="info"
              size="s"
              color="secondary"
            />
          </Flex>
          <BaseText
            v-test="'sale-document-total'"
            :color="document.totalAmountInclTaxExempt < 0 ? 'error' : 'default'"
          >
            {{ filters.currency(document.totalAmountInclTaxExempt) }}
          </BaseText>
        </Flex>
        <Flex>
          <BaseText
            size="s"
            color="secondary"
            iconBefore="calendar"
            v-test="'sale-document-issue-date'"
          >
            {{ filters.date(document.issuedAt) }}
          </BaseText>
          <div
            v-test="'sale-document-reference'"
            v-if="theReferencedDocument(document)"
          >
            <BaseText
              size="s"
              color="secondary"
              iconBefore="info"
              inline
            >
              {{ $t('global.reference') }}:
            </BaseText>
            <BaseText
              size="s"
              color="secondary"
              inline
              v-test="'sale-document-reference-number'"
              @click="viewReferencedDocument(theReferencedDocument(document)?.id)"
            >
              {{ theReferencedDocument(document)?.number }}
            </BaseText>
          </div>
        </Flex>
        <Flex
          justify="end"
          :class="$style.actions"
        >
          <BaseMore
            size="s"
            inline
            v-test="'sale-document-actions'"
            @select="onOptionSelect(document.id, $event)"
            :options="['view', 'print', 'send']"
          />
        </Flex>
      </BaseCard>
    </Flex>
  </BaseCard>
</template>

<script
  lang="ts"
  setup
>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import PrintUrl from '@/components/PrintUrl.vue';
import config from '@/config';
import filters from '@/filters';
import EmailReceipt from '@/modals/EmailReceipt.vue';
import { segment } from '@/segment';
import { useNativeAppStore } from '@/stores/native-app';
import {
  type Order,
  type RegisterDocument,
  RegisterDocumentEnum,
} from '@/types';

const { t } = useI18n();
const showEmailReceiptModal = ref(false);
const route = useRoute();
const router = useRouter();
const { isNativeApp } = useNativeAppStore();

const props = defineProps<{
  documents: (RegisterDocument & { legacy?: boolean })[];
  order: Order | null;
}>();

const receiptTypes = [
  RegisterDocumentEnum.Receipt,
  RegisterDocumentEnum.CorrectionReceipt,
];
const invoiceTypes = [
  RegisterDocumentEnum.Invoice,
  RegisterDocumentEnum.CreditInvoice,
];

const isReceiptType = (doc: RegisterDocument) =>
  receiptTypes.includes(doc.documentType as RegisterDocumentEnum);
const isInvoiceType = (doc: RegisterDocument) =>
  invoiceTypes.includes(doc.documentType as RegisterDocumentEnum);

// documents arrive latest-last from the API (SALON-2825); trust that order.
const filteredDocuments = computed(() =>
  props.documents.filter((document) => {
    if (showType.value === 'all') return true;
    if (showType.value === RegisterDocumentEnum.Receipt)
      return isReceiptType(document);
    if (showType.value === RegisterDocumentEnum.Invoice)
      return isInvoiceType(document);
    return document.documentType === showType.value;
  }),
);

const loadingDocumentId = ref<string | null>(null);

const theReferencedDocument = (document: RegisterDocument) => {
  return document.referencedDocuments[0];
};

const showType = ref('all');
const switchOptions = computed(() => [
  {
    label: t('global.all'),
    value: 'all',
    info: {
      label: props.documents.length,
    },
  },
  {
    label: t('global.items.receipt', 2),
    value: RegisterDocumentEnum.Receipt,
    info: {
      label: props.documents.filter(isReceiptType).length,
    },
  },
  {
    label: t('global.items.invoice', 2),
    value: RegisterDocumentEnum.Invoice,
    info: {
      label: props.documents.filter(isInvoiceType).length,
    },
  },
]);

const viewDocument = async (documentId: string | undefined) => {
  if (!documentId) return;
  loadingDocumentId.value = documentId;
  const url = `${config.backendUrl}/register/documents/${documentId}/view`;
  try {
    if (isNativeApp) {
      window.location.href = url;
    } else {
      // Open window synchronously to preserve user gesture (avoids popup blocking).
      // For print: /print embeds the PDF in an iframe and triggers window.print()
      // only after the PDF has fully loaded.
      window.open(url, '_blank');
    }
  } finally {
    loadingDocumentId.value = '';
  }
};

const promptPrint = ref(false);
const printKey = ref(0);
const printUrl = ref('');

const printDocument = async (
  document: RegisterDocument & { legacy?: boolean },
) => {
  loadingDocumentId.value = document.id;
  const url = document.legacy
    ? `${config.backendUrl}/orders/${route.params.id}`
    : `/register/documents/${document.id}/print`;
  try {
    if (isNativeApp) {
      // Native app: doing window.print() from a window.open() page prints the
      // opener instead of the document. Instead we need to navigate to the document page and print from there.
      // back=1 tells the document page to print the document and then go back to the previous page.
      window.location.href = `${url}?back=1`;
    } else {
      printUrl.value = url;
      printKey.value++;
      promptPrint.value = true;
    }
  } finally {
    loadingDocumentId.value = '';
  }
};

const getLegacyPdfUrl = () => {
  return `${config.backendUrl}/orders/${route.params.id}.pdf`;
};

const selectedDocumentId = ref<string | null>(null);

const onOptionSelect = (documentId: string, option: string) => {
  segment.track(`register.sales.documents.action.clicked`, {
    action_name: option,
  });

  const document = props.documents.find((d) => d.id === documentId);

  if (option === 'view') {
    document?.legacy
      ? window.open(getLegacyPdfUrl(), '_blank')
      : viewDocument(documentId);
  }
  if (option === 'print' && document) {
    printDocument(document);
  }
  if (option === 'send') {
    if (
      document?.documentType === RegisterDocumentEnum.Receipt ||
      document?.documentType === RegisterDocumentEnum.CorrectionReceipt
    ) {
      selectedDocumentId.value = document?.id as string;
      showEmailReceiptModal.value = true;
    } else {
      router.push({
        name: document?.legacy ? 'invoice-email' : 'document-email',
        params: document?.legacy
          ? {
              id: route.params.id as string,
            }
          : {
              documentId: document?.id as string,
              orderId: route.params.id as string,
            },
      });
    }
  }
};

const viewReferencedDocument = (documentId: string | undefined) => {
  segment.track('register.sales.documents.action.clicked', {
    action_name: 'reference',
  });
  viewDocument(documentId);
};

const noResultsKey = computed(
  () =>
    ({
      all: 'documents',
      [RegisterDocumentEnum.Receipt]: 'receipts',
      [RegisterDocumentEnum.Invoice]: 'invoices',
    })[showType.value],
);

watch(showType, () => {
  segment.track(`register.sales.documents.filter.clicked`, {
    filter_name: showType.value,
  });
});

const noResultsHeading = computed(() =>
  t(`register.documents.no_${noResultsKey.value}.heading`),
);
const noResultsText = computed(() =>
  t(`register.documents.no_${noResultsKey.value}.text`),
);
</script>

<style
  lang="scss"
  module
>
.actions {
  border-top: 1px solid var(--color-border);
  padding-top: $spacing;
  margin-top: $spacing;
}
</style>
